'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

type DocumentTopNavProps = {
  backLabel?: string;
  backHref?: string;
  onBack?: () => void;
  title?: string;
};

/** Minimal top bar for proof cards / certificates — avoids crowded landing nav overlap. */
export default function DocumentTopNav({
  backLabel = 'Back',
  backHref = '/individual-dashboard/explore?mode=archives',
  onBack,
  title,
}: DocumentTopNavProps) {
  const backContent = (
    <>
      <ArrowLeft size={16} />
      <span>{backLabel}</span>
    </>
  );

  return (
    <header className="fixed left-0 right-0 top-0 z-50 border-b border-slate-200/90 bg-white/95 backdrop-blur-md">
      <div className="mx-auto flex h-14 max-w-5xl items-center justify-between gap-4 px-4 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
            >
              {backContent}
            </button>
          ) : (
            <Link
              href={backHref}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
            >
              {backContent}
            </Link>
          )}
          <Link href="/" className="hidden items-center gap-2 sm:flex">
            <AppLogo size={28} />
            <span className="text-sm font-bold text-trust-blue">Ertzyx</span>
          </Link>
        </div>
        {title ? (
          <p className="hidden truncate text-center text-xs font-semibold text-slate-500 sm:block sm:max-w-[40%]">
            {title}
          </p>
        ) : (
          <span className="hidden flex-1 sm:block" />
        )}
        <div className="flex shrink-0 items-center gap-2">
          <Link
            href="/login"
            className="rounded-lg px-3 py-1.5 text-xs font-bold text-violet-700 transition-colors hover:bg-violet-50"
          >
            Log In
          </Link>
          <Link
            href="/register"
            className="rounded-lg bg-trust-blue px-3 py-1.5 text-xs font-bold text-white"
          >
            Claim Ledger
          </Link>
        </div>
      </div>
    </header>
  );
}
